import { showHttpError, showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'

import { startSubmitting, stopSubmitting, closePopup } from 'actions/integrations/disconnect_popup'
import { redirectToAuthorizeUrl } from 'actions/omniauth'
import { rootApi } from 'root-api'

export const connectPersonalZoomAccount = () => redirectToAuthorizeUrl('/users/auth/zoom_oauth2')
export const connectOrganizationZoomAccount = () => redirectToAuthorizeUrl('/users/auth/zoom_org_oauth2')

const FETCH_PERSONAL_ZOOM_ACCOUNT = 'fetchPersonalZoomAccount'
const FETCH_ORGANIZATION_ZOOM_ACCOUNT = 'fetchOrganizationZoomAccount'
const PERSONAL_ZOOM_ACCOUNT_TAG = 'PERSONAl_ZOOM_ACCOUNT'
const ORGANIZATION_ZOOM_ACCOUNT_TAG = 'ORGANIZATION_ZOOM_ACCOUNT'
const ORGANIZATION_ZOOM_USERS_TAG = 'ORGANIZATION_ZOOM_USERS_TAG'

const api = rootApi.enhanceEndpoints({
  addTagTypes: [
    PERSONAL_ZOOM_ACCOUNT_TAG, ORGANIZATION_ZOOM_ACCOUNT_TAG, ORGANIZATION_ZOOM_USERS_TAG
  ]
}).injectEndpoints({
  endpoints: (build) => ({
    [FETCH_PERSONAL_ZOOM_ACCOUNT]: build.query({
      query: () => ({
        url: '/api/integrations/zoom/account'
      }),
      providesTags: [PERSONAL_ZOOM_ACCOUNT_TAG],
      extraOptions: {
        handleErrors: false
      }
    }),
    disconnectPersonalZoomAccount: build.mutation({
      query: () => ({
        url: '/api/integrations/zoom/account',
        method: 'DELETE',
      }),
      onQueryStarted: async ({ reconnect = false } = {}, { dispatch, queryFulfilled }) => {
        dispatch(startSubmitting())

        try {
          await queryFulfilled

          if (reconnect) {
            await dispatch(connectPersonalZoomAccount())
          } else {
            dispatch(showSuccess(I18n.t('publisher.integrations.zoom.disconnect_success_notice')))
            dispatch(api.util.invalidateTags([PERSONAL_ZOOM_ACCOUNT_TAG]))
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }

        dispatch(stopSubmitting())
        dispatch(closePopup())
      }
    }),
    updatePersonalZoomAutoStartMeetingSummary: build.mutation({
      query: ({ autoStartMeetingSummaryEnabled }) => ({
        url: '/api/integrations/zoom/account',
        method: 'PATCH',
        data: { auto_start_meeting_summary_enabled: autoStartMeetingSummaryEnabled }
      }),
      onQueryStarted: async ({ autoStartMeetingSummaryEnabled }, lifecycleAPI) => {
        const optimisticUpdate = lifecycleAPI.dispatch(
          api.util.updateQueryData(FETCH_PERSONAL_ZOOM_ACCOUNT, undefined,
            (draft) => { draft.autoStartMeetingSummaryEnabled = autoStartMeetingSummaryEnabled })
        )

        try {
          const { data } = await lifecycleAPI.queryFulfilled
          lifecycleAPI.dispatch(api.util.upsertQueryData(FETCH_PERSONAL_ZOOM_ACCOUNT, undefined, data))
        } catch {
          optimisticUpdate.undo()
        }
      }
    }),
    [FETCH_ORGANIZATION_ZOOM_ACCOUNT]: build.query({
      query: () => ({
        url: '/api/integrations/zoom/organization/account'
      }),
      providesTags: [ORGANIZATION_ZOOM_ACCOUNT_TAG]
    }),
    disconnectOrganizationZoomAccount: build.mutation({
      query: () => ({
        url: '/api/integrations/zoom/organization/account',
        method: 'DELETE',
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        dispatch(startSubmitting())

        try {
          await queryFulfilled

          dispatch(showSuccess(I18n.t('publisher.integrations.zoom.disconnect_success_notice')))
          dispatch(api.util.invalidateTags([ORGANIZATION_ZOOM_ACCOUNT_TAG, ORGANIZATION_ZOOM_USERS_TAG]))
        } catch (e) {
          dispatch(showHttpError(e.error))
        }

        dispatch(stopSubmitting())
        dispatch(closePopup())
      }
    }),
    updateOrganizationZoomSettings: build.mutation({
      query: ({ allowDisconnection, autoStartMeetingSummaryEnabled }) => ({
        url: '/api/integrations/zoom/organization/account',
        method: 'PATCH',
        data: {
          allow_disconnect: allowDisconnection,
          auto_start_meeting_summary_enabled: autoStartMeetingSummaryEnabled,
        }
      }),
      onQueryStarted: async ({ allowDisconnection, autoStartMeetingSummaryEnabled }, lifecycleAPI) => {
        const optimisticUpdate = lifecycleAPI.dispatch(
          api.util.updateQueryData(FETCH_ORGANIZATION_ZOOM_ACCOUNT, undefined,
            (draft) => {
              if (allowDisconnection !== undefined) {
                draft.allowDisconnection = allowDisconnection
              }

              if (autoStartMeetingSummaryEnabled !== undefined) {
                draft.autoStartMeetingSummary = autoStartMeetingSummaryEnabled
              }
            })
        )

        try {
          const { data } = await lifecycleAPI.queryFulfilled
          lifecycleAPI.dispatch(api.util.upsertQueryData(FETCH_ORGANIZATION_ZOOM_ACCOUNT, undefined, data))
        } catch {
          optimisticUpdate.undo()
        }
      }
    }),
    fetchOrganizationZoomUsers: build.query({
      query: (params) => ({
        url: '/api/integrations/zoom/organization/account/users',
        params
      }),
      providesTags: [ORGANIZATION_ZOOM_USERS_TAG],
      extraOptions: {
        handleErrors: false
      }

    }),
    connectUser: build.mutation({
      query: () => ({
        url: '/api/integrations/zoom/organization/account/connect_user',
        method: 'POST',
      }),
      invalidatesTags: [ORGANIZATION_ZOOM_ACCOUNT_TAG]
    }),
    disconnectUser: build.mutation({
      query: () => ({
        url: '/api/integrations/zoom/organization/account/disconnect_user',
        method: 'DELETE',
      }),
      invalidatesTags: [ORGANIZATION_ZOOM_ACCOUNT_TAG]
    }),
    getPermissionRequest: build.query({
      query: (uuid) => ({
        url: '/api/integrations/zoom/organization/permission',
        params: { permissionUuid: uuid }
      }),
    }),
    generatePermissionRequest: build.mutation({
      query: () => ({
        url: '/api/integrations/zoom/organization/permission',
        method: 'POST'
      }),
    }),
  })
})

export const {
  useFetchPersonalZoomAccountQuery,
  useDisconnectPersonalZoomAccountMutation,
  useUpdatePersonalZoomAutoStartMeetingSummaryMutation,
  useFetchOrganizationZoomAccountQuery,
  useDisconnectOrganizationZoomAccountMutation,
  useUpdateOrganizationZoomSettingsMutation,
  useFetchOrganizationZoomUsersQuery,
  useConnectUserMutation,
  useDisconnectUserMutation,
  useGetPermissionRequestQuery,
  useGeneratePermissionRequestMutation,
} = api
